import { FC, useEffect, useMemo, useState } from "react"

import { DownOutlined, UpOutlined } from "@ant-design/icons"
import { Typography, Checkbox, Table, Collapse, Row, Col, Button, Space, TimePicker, Form, Alert, Modal } from "antd"
import dayjs from "dayjs"
import { unstable_useBlocker as useBlocker } from "react-router-dom"

import { ColorifiedButton } from "@/components/_Shared/Button/ColorifiedButton"
import { Resource, Scope } from "@/constants/permission"

import classes from "./CustomerSettingsPage.module.scss"

import { useDepotSelectListQuery } from "@/hook/Dictionaries/useDepotQuery"
import { useFuelTypeQuery } from "@/hook/Dictionaries/useFuelTypeQuery"
import { useCustomerSettingsMutation } from "@/hook/Settings/UseSettingsMutation"
import { useSettingsByUser } from "@/hook/Settings/UseSettingsQuery"
import { usePermission } from "@/hook/usePermission"

import { IDepot } from "@/types/IDepot"
import { IFuelType } from "@/types/IFuelType"
import { DayOfWeek, ICustomerNotificationSettings, ICustomerSettings, NotificationSettingType } from "@/types/ISettings"
import { ITableColumn } from "@/types/ui"


const { Title, Text } = Typography
const { Panel } = Collapse

const days = [
  { label: "Пн", value: DayOfWeek.Monday },
  { label: "Вт", value: DayOfWeek.Tuesday },
  { label: "Ср", value: DayOfWeek.Wednesday },
  { label: "Чт", value: DayOfWeek.Thursday },
  { label: "Пт", value: DayOfWeek.Friday },
  { label: "Сб", value: DayOfWeek.Saturday },
  { label: "Вс", value: DayOfWeek.Sunday }
]

const CustomerSettingsPage: FC = () => {
    const [settings, setSettings] = useState<ICustomerSettings | undefined>(undefined)
    const [from, setFrom] = useState<dayjs.Dayjs | null>(null)
    const [to, setTo] = useState<dayjs.Dayjs | null>(null)
    const [selectedDays, setSelectedDays] = useState<DayOfWeek[]>([])
    const [isShowPushNotification, setIsShowPushNotification] = useState(false)
    const [isOnlyImportantNotification, setIsOnlyImportantNotification] = useState(false)
    const [isNotDisturbMode, setIsNotDisturbMode] = useState(false)
    const [isChildrenPushDisabled, setIsChildrenPushDisabled] = useState(false)
    const [isChildrenEmailDisabled, setIsChildrenEmailDisabled] = useState(false)
    const [isChildrenVoiceDisabled, setIsChildrenVoiceDisabled] = useState(false)
    const [isChildrenImportantDisabled, setIsChildrenImportantDisabled] = useState(false)
    const [showModal, setShowModal] = useState(false)
    const [schedulErrorMessage, setSchedulErrorMessage] = useState<string | undefined>(undefined)
    const [isDirty, setIsDirty] = useState(false)
    const blocker = useBlocker(isDirty)

    const { hasPermission } = usePermission()
    

    const isAdminOrManager = useMemo(() => {
            return hasPermission(Resource.OrderReport, Scope.Read)
        }, [])

    const { updateCustomerSettingsMutation }  = useCustomerSettingsMutation()
    const { data: depotsData } = useDepotSelectListQuery()
    const { data: fuelTypeData } = useFuelTypeQuery()
    const { data: settingsData, refetch } = useSettingsByUser()

    const notificationSettingsTypeColumns: ITableColumn<ICustomerNotificationSettings>[] = [
        {
            title: "Категория",
            dataIndex: "statusText",
            key:"statusText",
            isVisible: true,
            order: 1,
            width: 300
        },
        {
            title: "Push",
            dataIndex: "isPushNotificationEnable",
            key:"isPushNotificationEnable",
            isVisible: true,
            width: 100,
            order: 2,
            render: (_,notificationSettings) => (
                <Checkbox 
            disabled={notificationSettings.status !== null  && isChildrenPushDisabled}
            checked={notificationSettings.isPushNotificationEnable}
            onChange={ (e) => {
              const checked = e.target.checked
              if (notificationSettings.children && notificationSettings.children.length > 0) {
                setIsChildrenPushDisabled(!checked)
              }
              updateNotificationSettings(
                notificationSettings.id,
                checked,
                notificationSettings.isEmailNotificationEnable,
                notificationSettings.isVoceEnable,
                notificationSettings.isImportant)
            }}/>
            ),
            align:"center"
        },
        {
            title: "Email",
            dataIndex: "isEmailNotificationEnable",
            key:"isEmailNotificationEnable",
            isVisible: true,
            order: 3,
            align:"center",
            render: (_,notificationSettings) => (
                <Checkbox 
              disabled={notificationSettings.status !== null  && isChildrenEmailDisabled}
              checked={notificationSettings.isEmailNotificationEnable}
              onChange={ (e)=> { 
                const checked = e.target.checked
                if (notificationSettings.children && notificationSettings.children.length > 0) {
                  setIsChildrenEmailDisabled(!checked)
                }
                updateNotificationSettings(
                  notificationSettings.id,
                   notificationSettings.isPushNotificationEnable,
                    checked,
                     notificationSettings.isVoceEnable,
                    notificationSettings.isImportant)
              }}
            />
            )
        },
        {
            title: "Звук",
            dataIndex: "isVoceEnable",
            key:"isVoceEnable",
            isVisible: true,
            order: 4,
            align:"center",
            render: (_,notificationSettings) => (
                <Checkbox 
              disabled={notificationSettings.status !== null  && isChildrenVoiceDisabled}
              checked={notificationSettings.isVoceEnable}
              onChange={ (e)=>{
                const checked = e.target.checked
                if (notificationSettings.children && notificationSettings.children.length > 0) {
                  setIsChildrenVoiceDisabled(!checked)
                }
                updateNotificationSettings(
                  notificationSettings.id,
                   notificationSettings.isPushNotificationEnable,
                    notificationSettings.isEmailNotificationEnable, 
                    checked, 
                    notificationSettings.isImportant)
              }}
            />
            )
        },
        {
            title: "Важное",
            dataIndex: "isImportant",
            key:"isImportant",
            isVisible: true,
            order: 5,
            align:"center",
            render: (_,notificationSettings) => (
                <Checkbox 
              disabled={notificationSettings.status !== null  && isChildrenImportantDisabled}
              checked={notificationSettings.isImportant}
              onChange={ (e)=>{
                const checked = e.target.checked
                if (notificationSettings.children && notificationSettings.children.length > 0) {
                  setIsChildrenImportantDisabled(!checked)
                }
                updateNotificationSettings(
                  notificationSettings.id,
                   notificationSettings.isPushNotificationEnable,
                    notificationSettings.isEmailNotificationEnable,
                     notificationSettings.isVoceEnable,
                    checked)
              }}
            />
            )
        }
    ]

    const depotSettingsColumns: ITableColumn<IDepot>[] = [
        {
            title: "Название",
            dataIndex: "name",
            key:"name",
            isVisible: true,
            order: 1,
            width: 200,
            align:"center"
        },
        {
            title: "Показывать",
            dataIndex: "isShow",
            key:"isShow",
            isVisible: true,
            order: 2,
            render: (_,depot) => (
                <Checkbox 
                checked={settings?.depotsIds?.includes(depot.id)} 
                onChange={()=>{updateDepotSettings(depot.id)}}/>
            ),
            align:"center"
        }
    ]

    const fuelTypeSettingsColumns: ITableColumn<IFuelType>[] = [
        {
            title: "Название",
            dataIndex: "name",
            key:"name",
            isVisible: true,
            order: 1,
            width: 200,
            align:"center"
        },
        {
            title: "Показывать",
            dataIndex: "isShow",
            key:"isShow",
            isVisible: true,
            order: 2,
            render: (_,fuelType) => (
                <Checkbox 
                checked={settings?.fuelTypesIds?.includes(fuelType.id)}
                onChange={()=>{updateFuelTypesSettings(fuelType.id)}} />
            ),
            align:"center"
        }
    ]
    
    const isScheduleValid: boolean = useMemo(()=> {
        return to !== null && from !== null && selectedDays.length > 0
    }, [to, from, selectedDays])

    useEffect( ()=> {
        if (JSON.stringify(settings) !== JSON.stringify(settingsData?.response) 
            || isNotDisturbMode && (from?.format("HH:mm:ss") !== settingsData?.response?.notificationSchedule?.from 
            || to?.format("HH:mm:ss") !== settingsData?.response?.notificationSchedule?.to 
            || selectedDays.sort().toString() !== settingsData?.response?.notificationSchedule?.notificationDays.sort().toString() 
            || isOnlyImportantNotification !== settingsData?.response?.notificationSchedule?.isOnlyImportantNotification)) {
            setIsDirty(true)
        }
        else {
            setIsDirty(false)
        }
    },[settings, from, to, selectedDays, isOnlyImportantNotification])

    useEffect(() => {
        if (blocker.state === "blocked") {
            setShowModal(true)
        }
    }, [blocker.state])

    useEffect(()=> {
        var statusSettings = settingsData?.response?.notificationSettings?.find(ns=>ns.settingType == NotificationSettingType.OrderStatus)

        if (statusSettings){
            setIsChildrenPushDisabled(!statusSettings.isPushNotificationEnable)
            setIsChildrenEmailDisabled(!statusSettings.isEmailNotificationEnable)
            setIsChildrenVoiceDisabled(!statusSettings.isVoceEnable)
            setIsChildrenImportantDisabled(!statusSettings.isImportant)
        }

        if(settingsData?.response?.notificationSchedule){
            const schedule = settingsData.response.notificationSchedule
            setFrom(dayjs(schedule.from, "HH:mm:ss"))
            setTo(dayjs(schedule.to, "HH:mm:ss"))
            setSelectedDays(schedule.notificationDays)
            setIsOnlyImportantNotification(schedule.isOnlyImportantNotification ?? false)
        }

        setIsShowPushNotification(settingsData?.response?.isShowPushNotification ?? false)
        setIsNotDisturbMode(settingsData?.response?.isNotDisturbMode ?? false)
        setSettings(structuredClone(settingsData?.response))
    }, [settingsData?.response])

    const updateDepotSettings = (id: number) => {
        const customerSettings = { ...settings }

        if (customerSettings) {
            if (customerSettings.depotsIds?.includes(id)) {
                customerSettings.depotsIds = customerSettings.depotsIds.filter(depotId => depotId !== id)
            } else {
                customerSettings.depotsIds = [...(customerSettings.depotsIds || []), id]
            }
            setSettings(customerSettings as ICustomerSettings)
        }
    } 

    const updateFuelTypesSettings = (id: number) => {
        const customerSettings = { ...settings }

        if (customerSettings) {
            if (customerSettings.fuelTypesIds?.includes(id)) {
                customerSettings.fuelTypesIds = customerSettings.fuelTypesIds.filter(depotId => depotId !== id)
            } else {
                customerSettings.fuelTypesIds = [...(customerSettings.fuelTypesIds || []), id]
            }
            setSettings(customerSettings as ICustomerSettings)
        }
    } 

    const updateNotificationSettings  = (id:number, isPushNotificationEnable: boolean, isEmailNotificationEnable: boolean, isVoceEnable: boolean, isImportant: boolean ) => {
        const customerSettings = { ...settings }

        if (customerSettings.notificationSettings) {
            let notificationSetting = customerSettings.notificationSettings.find(ns=>ns.id == id)

            if (!notificationSetting){
                notificationSetting = customerSettings.notificationSettings.find(ns=> ns.children && ns.children.find(c=>c.id == id))?.children?.find(c=>c.id == id)
            }

            if (notificationSetting){
                const isPush = notificationSetting.isPushNotificationEnable

                notificationSetting.isPushNotificationEnable = isImportant && !notificationSetting.isImportant ? isImportant : isPushNotificationEnable
                notificationSetting.isEmailNotificationEnable = isEmailNotificationEnable
                notificationSetting.isVoceEnable = isVoceEnable
                notificationSetting.isImportant = !isPushNotificationEnable && isPush ? isPushNotificationEnable : isImportant
            }
        }
        
        setSettings(customerSettings as ICustomerSettings)
    }

    const onSaveSettings = async () => {
        const settingsResult: ICustomerSettings = {
            ...settings,
            isShowPushNotification: isShowPushNotification,
            isNotDisturbMode: isNotDisturbMode
        }

        if(isNotDisturbMode){
            if(!validateSchedule()){
                return
            }

            settingsResult.notificationSchedule = {
                id: settings?.notificationSchedule?.id ??    0,
                from: from!.format("HH:mm:ss"),
                to: to!.format("HH:mm:ss"),
                isOnlyImportantNotification: isOnlyImportantNotification,
                customerSettingsId: settings?.id ?? 0,
                notificationDays: selectedDays
            }
        }

        await updateCustomerSettingsMutation.mutateAsync(settingsResult)
        await refetch()
        setIsDirty(false)
    }
    
    const validateSchedule = (): boolean => {
        if (!from || !to) {
            setSchedulErrorMessage("Укажите время показа уведомлений")
            return false
        }
        if (to.isBefore(from) || to.isSame(from)) {
            setSchedulErrorMessage("Время «До» не может быть равно или меньше времени «С»")
            return false
        }
        if (selectedDays.length == 0) {
            setSchedulErrorMessage("Выберите хотя бы один день недели")
            return false
        }
        return true
    }

    const onCancel = async () => {
        if (blocker.state !== "blocked") return

        setShowModal(false)
        blocker.proceed()
    }

    const onConfirmLeave = async () => {
        if (blocker.state !== "blocked") return
        if(!validateSchedule() && isNotDisturbMode){
            setShowModal(false)
            blocker.reset()
            return
        }
        else{
            await onSaveSettings()
            setShowModal(false)
            blocker.proceed()
        }

    }
    
    return (
        <div>
            <Modal
              open={showModal}
              title="Сохранить настройки?"
              onCancel={() => {
                if (blocker.state !== "blocked") return
                blocker.reset()
                setShowModal(false)
              }}
              footer={
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%"
                  }}
                >
                  <Button type="primary" onClick={onCancel}>
                    Отменить и уйти
                  </Button>

                  <ColorifiedButton
                    themecolor="orange"
                    type="primary"
                    onClick={onConfirmLeave}
                  >
                    Сохранить и уйти
                  </ColorifiedButton>
                </div>
              }
            >
              <p>Несохраненные настройки будут утеряны</p>
            </Modal>
            <Row justify="space-between" align="middle" style={{ padding: "20px 0" }}>
                <Col>
                    <Title level={3}>Настройки</Title>
                </Col>
                <Col>
                    <Button onClick={onSaveSettings} type="primary"  className={classes["save-btn"]}>
                        Сохранить
                    </Button>
                 </Col>
            </Row>
            {!isAdminOrManager && (
              <>
              <Row className={classes["order-row"]} style={{ width : "100%" }}>
                <Title level={4} >НБ отображения в таблице цен/лимитов</Title>
                <Table 
                scroll={{ x: "max-content", y: "calc(100vh - 350px)" }}
                dataSource={depotsData?.response}
                columns={depotSettingsColumns}
                rowKey={"name"}
                pagination={false}/>
            </Row>
            <Row className={classes["order-row"]} style={{ width : "100%" }}>
                <Title level={4} >Выбор вида топлива отображения в таблице цен/лимитов</Title>
                <Table 
                scroll={{ x: "max-content", y: "calc(100vh - 350px)" }}
                dataSource={fuelTypeData?.response.data}
                columns={fuelTypeSettingsColumns}
                rowKey={"name"}
                pagination={false}/>
            </Row>
              </>
            )}
            <Row className={classes["order-row"]}>
                <Title level={4}>Настройки уведомлений</Title>
                <Table 
                scroll={{ x: "max-content", y: "calc(100vh - 350px)" }}
                dataSource={settings?.notificationSettings}
                columns={notificationSettingsTypeColumns}
                rowKey={"id"}
                pagination={false}
                expandable={{
                  expandIcon: ({ expanded, onExpand, record }) =>
                    record.children && record.children.length > 0 ? ( 
                      <span
                        onClick={(e) => onExpand(record, e)}
                        style={{ cursor: "pointer" }}
                      >
                        {expanded ? <UpOutlined /> : <DownOutlined />}
                      </span>
                    ) : null 
                }}
                summary={() => (
                  <Table.Summary fixed>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} align="left" >
                        Всплывающие уведомления
                      </Table.Summary.Cell>
                      <Table.Summary.Cell
                        index={1}
                        colSpan={notificationSettingsTypeColumns.length - 2}
                      />
                      <Table.Summary.Cell index={notificationSettingsTypeColumns.length - 1} align="center">
                        <Checkbox
                        checked={isShowPushNotification}
                        onChange={(e) => {
                            setIsShowPushNotification(e.target.checked)
                            setSettings((prevSettings) => ({
                                ...prevSettings!,
                                isShowPushNotification: e.target.checked
                            }))
                        }}
                      />
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                )}/>
            </Row>
            <Row className={classes["order-row"]}>
                <Col span={24}>
                    <Title level={4}>Расписание показа уведомлений</Title>
                </Col>
                <Col span={24}>
                <Collapse 
                defaultActiveKey={settings?.isNotDisturbMode ? ["1"] : undefined}
                onChange={(activeKeys) => {
                    const isOpen = activeKeys.includes("1")
                    if (!isOpen && !isNotDisturbMode) {
                        setSelectedDays([])
                        setTo(null)
                        setFrom(null)
                        setIsOnlyImportantNotification(false)   
                    }
                    }} >
                  <Panel
                  key="1"  
                  header={
                    <Row justify="space-between" align="middle" style={{ width: "100%" }}>
                      <Col>
                        Режим «Не беспокоить»
                      </Col>
                      <Col>
                        <Checkbox
                        checked={isNotDisturbMode}
                        disabled={!isScheduleValid}
                        onChange={(e) => {
                            setIsNotDisturbMode(e.target.checked)
                            setSettings((prevSettings) => ({
                                ...prevSettings!,
                                isNotDisturbMode: e.target.checked,
                                notificationSchedule: e.target.checked ? prevSettings!.notificationSchedule : undefined
                            }))
                            if(!e.target.checked){
                                setTo(null)
                                setFrom(null)
                                setSelectedDays([])
                                setIsOnlyImportantNotification(false)
                            }
                        }}
                          onClick={(e) => e.stopPropagation()} 
                        />
                      </Col>
                    </Row>
                  }>
                    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                      <Row style={{ background: "#f5faff", padding: "8px 16px", borderRadius: 6 }}>
                        <Col span={6}>
                          <Text type="secondary">Время</Text>
                        </Col>
                        <Col span={18}>
                          <Text type="secondary">Дни недели</Text>
                        </Col>
                      </Row>
                      <Row gutter={24} align="middle">
                        <Col span={6}>
                          <Space direction="vertical" size="middle">
                            <Space>
                              <Text>С</Text>
                              <TimePicker
                                value={from}
                                onChange={setFrom}
                                format="HH:mm"
                              />
                            </Space>

                            <Space>
                              <Text>До</Text>
                              <TimePicker
                                value={to}
                                onChange={setTo}
                                format="HH:mm"
                              />
                            </Space>
                          </Space>
                        </Col>
                        <Col span={18}>
                          <Space wrap size="small">
                            {days.map((day) => (
                              <Button
                                key={day.value}
                                type={selectedDays.includes(day.value) ? "primary" : "default"}
                                onClick={() =>
                                  setSelectedDays((prev) =>
                                    prev.includes(day.value)
                                      ? prev.filter((d) => d !== day.value)
                                      : [...prev, day.value]
                                  )
                                }
                              >
                                {day.label}
                              </Button>
                            ))}
                          </Space>
                        </Col>
                      </Row>
                      {schedulErrorMessage && (
                        <Col span={24}>
                            <Alert
                            message={schedulErrorMessage}
                            type="error" showIcon 
                            />
                        </Col> 
                      )}                   
                      <Checkbox
                        checked={isOnlyImportantNotification}
                        onChange={(e) => {
                            setIsOnlyImportantNotification(e.target.checked)
                            if(settings?.notificationSchedule){
                                setSettings((prevSettings) => ({
                                ...prevSettings!,
                                notificationSchedule:{
                                    ...prevSettings!.notificationSchedule!,
                                    isOnlyImportantNotification: e.target.checked
                                }
                            }))
                            }
                        }}
                      >
                        Показывать только важные уведомления
                      </Checkbox>
                    </Space>
                  </Panel>
                </Collapse>
                </Col>
            </Row>
        </div>
    )
}

export default CustomerSettingsPage